import { append, pathOr } from "ramda";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SpreadCalculation } from "../redux/decoders";
import { Selectors as groupSelector } from "../redux/modules/group";
import { getMarketGroupsLoadingAction } from "../redux/modules/marketGroups";
import { getSpreadCalculationsLoadingAction, Selectors as spreadCalculationsSelector } from "../redux/modules/spreadCalculation";
import { foldTag } from "../utils/fold";
import styles from "./spreadConfiguration.module.scss";

const formula: string =
  `Formula:
TotalAsk = +AlfaAsk + (x% × Price)
TotalBid = -AlfaBid - (y% × Price)

PrezzoAsk = Price + TotalAsk
PrezzoBid = Price + TotalBid
`;

export type SpreadCalculationTableProps = {
  selectedGroup: any,
  setSaveFlag: any,
  setSaveTable: any
};

export function SpreadCalculationTable(props: SpreadCalculationTableProps) {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getSpreadCalculationsLoadingAction())
    dispatch(getMarketGroupsLoadingAction(pathOr(0, ["selectedGroup", "groupId"], props)))
  }, []);

  const spreadCalculationsList = useSelector(
    spreadCalculationsSelector.all
  );

  useEffect(() => {
    console.log("spreadCalculationsList", spreadCalculationsList)
    setSpreadCalculationList(pathOr([], ["data"], spreadCalculationsList))
  }, [spreadCalculationsList]);

  const groupListState = useSelector(groupSelector.all);
  const groupList = useMemo(() => {
    if (groupListState.tag === "loaded") {
      return groupListState.data;
    }

    return [];
  }, [groupListState])

  const [spreadCalculationList, setSpreadCalculationList] = useState<SpreadCalculation[]>([]);

  const usedGroupIds = useMemo(() => {
    return spreadCalculationList
      .map((spreadCalc: SpreadCalculation) => spreadCalc.groupId)
      .filter((groupId) => !!groupId)
      ;
  }, [spreadCalculationList]);

  const updateEntry = (keyName: keyof SpreadCalculation, newValue: any, index: number) => {
    const newState = spreadCalculationList.map((obj, idx) => {

      if (idx === index) {
        return { ...obj, [keyName]: newValue };
      }

      return obj;
    });

    setSpreadCalculationList(newState);
    props.setSaveTable(newState)
    props.setSaveFlag(true)
  };

  const deleteEntry = (index: number) => {
    const newState = [...spreadCalculationList];
    newState.splice(index, 1);

    setSpreadCalculationList(newState);
    props.setSaveTable(newState)
    props.setSaveFlag(true)
  };

  return (
    <>
      <div>
        <pre style={{ marginInlineStart: '1rem' }}>{formula}</pre>
      </div>

      <button
        className={styles.addNewButton}
        onClick={() => {
          setSpreadCalculationList(append(spreadCalculationDefault, spreadCalculationList))

        }}
        disabled={spreadCalculationList.length >= groupList.length}
      >
        <span className={styles.plusMinusIcon}>+</span> Add New Spred Calculation
      </button>

      <table>

        <thead>
          <tr>
            <th></th>
            <th></th>
            <th colSpan={2}>x(%)</th>
            <th colSpan={2}>y(%)</th>
            <th colSpan={2}>Alfa Ask</th>
            <th colSpan={2}>Alfa Bid</th>
          </tr>
          <tr>
            <th></th>
            <th>Group</th>
            <th>Power</th>
            <th>Gas</th>
            <th>Power</th>
            <th>Gas</th>
            <th>Power</th>
            <th>Gas</th>
            <th>Power</th>
            <th>Gas</th>
          </tr>
        </thead>

        <tbody>
          {foldTag(spreadCalculationsList, {
            error: () => null,
            loading: () => (
              <tr className={styles.tableLoadingContainer}>
                <td colSpan={100} style={{ textAlign: "center" }}>
                  <div
                    style={{ width: "100%", height: "100%" }}
                    className="fa fa-spinner fa-spin"
                  ></div>
                </td>
              </tr>),
            initial: () => null,
            loaded: () => {
              return (
                <>
                  {spreadCalculationList.map(
                    (spreadCalc: SpreadCalculation, spreadCalcIndex: number) => {
                      return <tr key={spreadCalcIndex + "-group_Id"}>
                        <td
                          className={styles.plusMinusIcon}
                          onClick={() => deleteEntry(spreadCalcIndex)}
                        >
                          <div style={{ padding: ".25rem", textAlign: "center" }}>
                            −
                          </div>
                        </td>

                        <td>
                          <div style={{ padding: ".25rem" }}>
                            <select
                              id="groupDropdown"
                              defaultValue={spreadCalc.groupId!}
                              value={spreadCalc.groupId!}
                              onChange={e => updateEntry("groupId", Number(e.target.value), spreadCalcIndex)}
                              style={{ width: "10rem" }}
                            >
                              <option>Group</option>
                              {
                                groupList
                                  .filter(g => spreadCalc.groupId === g.group_Id || !usedGroupIds.includes(g.group_Id))
                                  .map(
                                    (group: any, index: number) => (
                                      <option key={index} value={group.group_Id}>{group.group_Name}</option>
                                    )
                                  )
                              }
                            </select>
                          </div>
                        </td>

                        <InputCell keyName="powerAsk" spreadCalc={spreadCalc} index={spreadCalcIndex} updateEntry={updateEntry} />
                        <InputCell keyName="gasAsk" spreadCalc={spreadCalc} index={spreadCalcIndex} updateEntry={updateEntry} />

                        <InputCell keyName="powerBid" spreadCalc={spreadCalc} index={spreadCalcIndex} updateEntry={updateEntry} />
                        <InputCell keyName="gasBid" spreadCalc={spreadCalc} index={spreadCalcIndex} updateEntry={updateEntry} />

                        <InputCell keyName="powerAlfaAsk" spreadCalc={spreadCalc} index={spreadCalcIndex} updateEntry={updateEntry} />
                        <InputCell keyName="gasAlfaAsk" spreadCalc={spreadCalc} index={spreadCalcIndex} updateEntry={updateEntry} />

                        <InputCell keyName="powerAlfaBid" spreadCalc={spreadCalc} index={spreadCalcIndex} updateEntry={updateEntry} />
                        <InputCell keyName="gasAlfaBid" spreadCalc={spreadCalc} index={spreadCalcIndex} updateEntry={updateEntry} />
                      </tr>
                    }
                  )}
                </>
              )
            }
          })}
        </tbody>

      </table>
    </>
  )
}

var spreadCalculationDefault: SpreadCalculation = {
  groupId: undefined,
  powerAsk: 0,
  powerBid: 0,
  gasAsk: 0,
  gasBid: 0,
  gasAlfaAsk: 0,
  gasAlfaBid: 0,
  powerAlfaAsk: 0,
  powerAlfaBid: 0
}

type InputCellProps = {
  index: number,
  keyName: keyof SpreadCalculation,
  spreadCalc: SpreadCalculation,
  updateEntry: (keyName: keyof SpreadCalculation, newValue: any, group_Id: number) => void
}

function InputCell(props: InputCellProps) {
  const { index, keyName, spreadCalc, updateEntry } = props;

  return <td>
    <div style={{ padding: ".25rem" }}>
      <input
        type="number"
        style={{ width: "100%" }}
        value={spreadCalc[keyName]?.toString()}
        onChange={e => updateEntry(
          keyName,
          Number(e.target.value),
          index,
        )}
      />
    </div>
  </td>
}